// src/components/SimpleComponents/SimpleSelectAsyncNcm.jsx
// *******************************************************************************
// ASYNC SELECT FOR NCM (Nomenclatura Comum do Mercosul)
// *******************************************************************************
// Async select component for searching NCM codes from the fiscal database.
// Uses debounced search against GET /v1/fiscal/ncm/search?q=xxx&level=8.
// Exact same pattern as SimpleSelectAsyncProducts — only the API and labels differ.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Select, Spin, Flex, Typography, Empty, Form } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useSimpleFormContext } from './SimpleFormProvider';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import axiosService from 'services/axiosService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DEBOUNCE_DELAY = 400;
const MIN_SEARCH_LENGTH = 2;
const MAX_RESULTS = 20;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleSelectAsyncNcm = ({
  name,
  subdocumentField,
  style = {},
  styleItem = {},
  disabled = false,
  placeholder = 'search_ncm',
  onChange,
  onNcmSelect,
  onBlur,
  validateTrigger = 'onBlur',
  showFeedback = false,
  required: requiredProp,
  minSearchLength = MIN_SEARCH_LENGTH,
  debounceDelay = DEBOUNCE_DELAY,
  maxResults = MAX_RESULTS,
  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, disabledFields, shouldShowErrors, requiredFields, form } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;
  const debounceTimerRef = useRef(null);
  const lastSearchRef = useRef('');

  // Watch field value for validation
  const fieldValue = Form.useWatch(fullFieldName, form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);
  const required = requiredProp ?? requiredFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [shouldValidate, setShouldValidate] = useState(false);
  const [customValidationStatus, setCustomValidationStatus] = useState('');
  const [customValidationMessage, setCustomValidationMessage] = useState('');

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Load full NCM data for pre-existing value (editing mode)
  useEffect(() => {
    if (!fieldValue) return;

    // Check if this value already has full data (with hierarchy)
    const existingOption = options.find(opt => opt.value === fieldValue && opt.ncm?.hierarchy);
    if (existingOption) return;

    // Set placeholder immediately so Select shows the formatted code
    setOptions(prev => {
      if (prev.find(opt => opt.value === fieldValue && opt.ncm?.hierarchy)) return prev;
      const hasPlaceholder = prev.find(opt => opt.value === fieldValue);
      if (!hasPlaceholder) return [{ value: fieldValue, label: formatNcmCode(fieldValue), ncm: null }, ...prev];
      return prev;
    });

    // Fetch full data from API to get hierarchy
    const fetchNcmData = async () => {
      try {
        const cleanCode = String(fieldValue).replace(/\D/g, '');
        const url = `${API.FISCAL.NCM_SEARCH}?search=${encodeURIComponent(cleanCode)}&limit=5&level=8`;
        const [result] = await axiosService.get({ url, returnAsObject: true });
        const items = result?.data || [];
        const match = items.find(item => item.codeNumeric === cleanCode);
        if (match) {
          setOptions(prev => {
            const filtered = prev.filter(opt => opt.value !== fieldValue);
            return [{ value: match.codeNumeric, label: formatNcmCode(match.codeNumeric), ncm: match }, ...filtered];
          });
        }
      } catch (error) {
        // Silently fail — placeholder is already showing
      }
    };
    fetchNcmData();
  }, [fieldValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Activate validation when form shows errors
  useEffect(() => {
    if (shouldShowErrors) setShouldValidate(true);
  }, [shouldShowErrors]);

  // Validate field value
  useEffect(() => {
    if (!shouldValidate) return;

    const hasValue = fieldValue !== undefined && fieldValue !== null && fieldValue !== '';

    if (!hasValue && required) {
      setCustomValidationStatus('error');
      setCustomValidationMessage(translateX('required_field'));
      return;
    }

    if (hasValue) {
      setCustomValidationStatus('success');
      setCustomValidationMessage('');
      return;
    }

    setCustomValidationStatus('');
    setCustomValidationMessage('');
  }, [fieldValue, required, shouldValidate]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(
    async searchValue => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (!searchValue || searchValue.length < minSearchLength) {
        setOptions([]);
        setLoading(false);
        return;
      }

      const normalizedSearch = searchValue.replace(/\./g, '').toLowerCase().trim();
      if (normalizedSearch === lastSearchRef.current) return;

      setLoading(true);

      debounceTimerRef.current = setTimeout(async () => {
        try {
          lastSearchRef.current = normalizedSearch;

          const url = `${API.FISCAL.NCM_SEARCH}?search=${encodeURIComponent(normalizedSearch)}&limit=${maxResults}&level=8`;
          const [result] = await axiosService.get({ url, returnAsObject: true });

          const items = result?.data || [];

          setOptions(
            items.map(item => ({
              value: item.codeNumeric,
              label: formatNcmCode(item.codeNumeric),
              ncm: item,
            })),
          );
        } catch (error) {
          console.error('[SimpleSelectAsyncNcm] Search error:', error);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, debounceDelay);
    },
    [minSearchLength, maxResults, debounceDelay],
  );

  const handleSelect = useCallback(
    (value, option) => {
      setOpen(false);
      setShouldValidate(true);

      if (typeof onChange === 'function') onChange(value, option);
      if (typeof onNcmSelect === 'function' && option?.ncm) onNcmSelect(option.ncm);
    },
    [onChange, onNcmSelect],
  );

  const handleDropdownVisibleChange = useCallback(visible => {
    setOpen(visible);
    if (!visible) lastSearchRef.current = '';
  }, []);

  const handleBlur = useCallback(
    ev => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (typeof onBlur === 'function') setTimeout(() => onBlur(ev), 0);
    },
    [onBlur],
  );

  const handleClear = useCallback(() => {
    setOptions([]);
    lastSearchRef.current = '';
    setShouldValidate(true);

    if (typeof onChange === 'function') onChange(null, null);
    if (typeof onNcmSelect === 'function') onNcmSelect(null);
  }, [onChange, onNcmSelect]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const notFoundContent = useMemo(() => {
    if (loading) {
      return (
        <Flex justify="center" style={{ padding: 16 }}>
          <Spin size="small" />
        </Flex>
      );
    }

    if (lastSearchRef.current && options.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={translateX('no_results_found')}
          style={{ margin: '16px 0' }}
        />
      );
    }

    return (
      <Flex justify="center" style={{ padding: 16 }}>
        <Text type="secondary">{translateX('type_to_search_ncm')}</Text>
      </Flex>
    );
  }, [loading, options.length]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      style={styleItem}
      validateTrigger={validateTrigger}
      showFeedback={showFeedback}
      required={required}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
    >
      <Select
        ref={elementRef}
        id={flattenFieldName}
        style={{ height: 40, ...style }}
        showSearch
        allowClear
        disabled={isDisabled}
        placeholder={translateX(placeholder)}
        filterOption={false}
        onSearch={handleSearch}
        onSelect={handleSelect}
        onClear={handleClear}
        onBlur={handleBlur}
        open={open}
        onOpenChange={handleDropdownVisibleChange}
        notFoundContent={notFoundContent}
        loading={loading}
        suffixIcon={loading ? <Spin size="small" /> : <SearchOutlined />}
        options={options}
        optionRender={option => {
          const ncm = option.data.ncm;
          if (!ncm) return option.label;

          // Build hierarchy breadcrumb from parent descriptions (levels 4 → 6 → 8)
          const hierarchy = ncm.hierarchy || [];
          // Parents = all except last (which is the item itself)
          const parents = hierarchy.slice(0, -1);

          return (
            <Flex vertical style={{ width: '100%', padding: '4px 0' }}>
              <Text strong style={{ lineHeight: 1.3 }}>
                {formatNcmCode(ncm.codeNumeric)}
              </Text>
              {parents.length > 0 && (
                <Text
                  type="secondary"
                  style={{ fontSize: 11, lineHeight: 1.2, color: '#8c8c8c' }}
                  ellipsis
                >
                  {parents.map(p => p.description).join(' / ')}
                </Text>
              )}
              <Text type="secondary" ellipsis style={{ fontSize: 12, lineHeight: 1.3 }}>
                {ncm.description}
              </Text>
            </Flex>
          );
        }}
        labelInValue={false}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleSelectAsyncNcm;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Format NCM code with dots: 12345678 → 1234.56.78
 */
function formatNcmCode(code) {
  if (!code) return '';
  const digits = String(code).replace(/\D/g, '');
  if (digits.length !== 8) return digits;
  return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6, 8)}`;
}
